import { ref, onMounted, onBeforeUnmount } from 'vue'
import { Modal } from 'bootstrap'

/**
 * Devolve a rolagem da página quando um modal é desmontado antes de terminar de fechar.
 *
 * O Bootstrap só tira a classe `modal-open` do body dentro do callback de transição do
 * backdrop. Fechar o modal e navegar na mesma ação desmonta o componente antes disso:
 * o `dispose()` interrompe a transição, o backdrop é removido, mas a classe permanece —
 * e a próxima tela abre travada, sem rolagem.
 *
 * Só limpa quando não sobrou nenhum outro modal aberto, para não destravar a página por
 * baixo de um modal empilhado.
 *
 * @param {HTMLElement|null} elementoAtual Modal sendo desmontado, ignorado na checagem.
 */
function releaseBodyScroll(elementoAtual) {
  const outroModalAberto = Array.from(document.querySelectorAll('.modal.show'))
    .some(el => el !== elementoAtual)

  if (outroModalAberto) return

  document.body.classList.remove('modal-open')
  document.body.style.removeProperty('overflow')
  document.body.style.removeProperty('padding-right')
  document.querySelectorAll('.modal-backdrop').forEach(el => el.remove())
}

export function onEventoDoModal(el, tipo, handler) {
  if (!el) return () => {}

  const doProprioModal = (event) => {
    if (event.target === el) handler(event)
  }

  el.addEventListener(tipo, doProprioModal)
  return () => el.removeEventListener(tipo, doProprioModal)
}

/**
 * Encapsula o ciclo de vida de um modal do Bootstrap.
 * Use `modalRef` no elemento `.modal` e chame `open()` / `close()`.
 *
 * @example
 * const { modalRef, open, close } = useBootstrapModal()
 */
export function useBootstrapModal() {
  const modalRef = ref(null)
  let instance = null

  // Um modal que nunca foi aberto não travou o body nem criou backdrop. Limpar no
  // desmonte dele apagaria o backdrop de OUTRO modal que ainda está na animação de
  // abertura — e isso acontece de verdade: todo DynamicForm carrega dois modais
  // fechados, então trocar o :key de um formulário enquanto outro modal abre
  // desmontaria esses dois e deixaria o modal recém-aberto sem escurecimento.
  let foiAberto = false

  let pararDeEscutar = () => {}

  const aoFechar = () => {
    foiAberto = false

    const aindaAbertos = document.querySelectorAll('.modal.show')
    aindaAbertos[aindaAbertos.length - 1]?.focus({ preventScroll: true })
  }

  onMounted(() => {
    if (!modalRef.value) return

    instance = new Modal(modalRef.value)
    pararDeEscutar = onEventoDoModal(modalRef.value, 'hidden.bs.modal', aoFechar)
  })

  onBeforeUnmount(() => {
    pararDeEscutar()
    instance?.dispose()

    if (foiAberto) {
      releaseBodyScroll(modalRef.value)
    }
  })

  const open = () => {
    if (!instance) return

    foiAberto = true
    instance.show()
  }

  const close = () => instance?.hide()

  return { modalRef, open, close }
}
